import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useRequireSiteManager } from "@/lib/role-hooks";
import { trpc } from "@/utils/trpc";

type NotificationSettingsData = {
  operationalEmails: boolean;
};

const NOTIFICATION_SETTING_FIELD = {
  key: "operationalEmails",
  title: "Operational emails",
  description: "Receive daily reports and compliance alerts by email",
} as const;

function normalizeNotificationSettings(
  value:
    | {
        operationalEmails?: boolean;
        emailNotifications?: boolean;
      }
    | undefined
): NotificationSettingsData {
  return {
    operationalEmails:
      value?.operationalEmails ?? value?.emailNotifications ?? true,
  };
}

export function NotificationSettings() {
  const { hasAccess, isLoading: isRoleLoading } = useRequireSiteManager();
  const queryClient = useQueryClient();
  const notificationSettingsQueryOptions =
    trpc.getNotificationSettings.queryOptions();
  const settingsQuery = useQuery({
    ...notificationSettingsQueryOptions,
    enabled: hasAccess,
  });
  const overviewQuery = useQuery({
    ...trpc.getSubscriptionOverview.queryOptions(),
    enabled: hasAccess,
  });
  const canReceiveOperationalEmails =
    overviewQuery.data?.capabilities.canReceiveOperationalEmails ?? true;
  const notificationSettings = normalizeNotificationSettings(
    settingsQuery.data
  );

  const toggleNotificationMutation = useMutation({
    ...trpc.toggleNotificationSettings.mutationOptions(),
    onMutate: async (nextSettings) => {
      await queryClient.cancelQueries({
        queryKey: notificationSettingsQueryOptions.queryKey,
      });

      const previousSettings = queryClient.getQueryData(
        notificationSettingsQueryOptions.queryKey
      );

      queryClient.setQueryData(
        notificationSettingsQueryOptions.queryKey,
        (current) =>
          current
            ? {
                ...current,
                operationalEmails:
                  nextSettings.operationalEmails ?? current.operationalEmails,
              }
            : current
      );

      return { previousSettings };
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(
        notificationSettingsQueryOptions.queryKey,
        (current) =>
          current
            ? {
                ...current,
                operationalEmails: data.settings.operationalEmails,
              }
            : current
      );
    },
    onError: (error, _variables, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(
          notificationSettingsQueryOptions.queryKey,
          context.previousSettings
        );
      }

      toast.error(error.message || "Failed to update notification settings");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: notificationSettingsQueryOptions.queryKey,
      });
    },
  });

  const handleNotificationToggle = (
    setting: keyof NotificationSettingsData
  ) => {
    toggleNotificationMutation.mutate({
      ...notificationSettings,
      [setting]: !notificationSettings[setting],
    });
  };

  if (isRoleLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3.5 w-64 max-w-full" />
            </div>
            <Skeleton className="h-8 w-14 shrink-0" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasAccess) {
    return null;
  }

  if (settingsQuery.isLoading && !settingsQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3.5 w-64 max-w-full" />
            </div>
            <Skeleton className="h-8 w-14 shrink-0" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (settingsQuery.error && !settingsQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Notification settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            Failed to load notification settings.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification settings</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium">
                {NOTIFICATION_SETTING_FIELD.title}
              </div>
              <div className="text-sm text-muted-foreground">
                {canReceiveOperationalEmails
                  ? NOTIFICATION_SETTING_FIELD.description
                  : "Requires Core plan. Upgrade to receive daily reports and compliance alerts by email."}
              </div>
            </div>
            <Button
              variant={
                notificationSettings[NOTIFICATION_SETTING_FIELD.key] &&
                canReceiveOperationalEmails
                  ? "default"
                  : "outline"
              }
              size="sm"
              onClick={() =>
                handleNotificationToggle(NOTIFICATION_SETTING_FIELD.key)
              }
              disabled={
                toggleNotificationMutation.isPending ||
                !canReceiveOperationalEmails
              }
            >
              {notificationSettings[NOTIFICATION_SETTING_FIELD.key] &&
              canReceiveOperationalEmails
                ? "On"
                : "Off"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
